import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import Login from './pages/Login';
import StatsBoard from './pages/StatsBoard';
import BillJobs from './pages/BillJobs';
import BulkDownloads from './pages/BulkDownloads';
import Viewer from './pages/Viewer';
import ConsolidatedViewer from './pages/ConsolidatedViewer';
import Settings from './pages/Settings';
import SuperAdmin from './pages/SuperAdmin';
import Cockpit from './pages/Cockpit';
import UbmLocations from './pages/ubm/Locations';
import UbmSpaces from './pages/ubm/Spaces';
import UbmMeters from './pages/ubm/Meters';
import UbmVendorAccounts from './pages/ubm/VendorAccounts';
import EntitiesPage from './pages/ubm/EntitiesPage';
import PaymentVerification from './pages/PaymentVerificationV2';
import PaymentFileView from './pages/PaymentFileViewV2';
import PaymentConfig from './pages/PaymentConfig';
import PaymentsV2Page from './pages/payments/PaymentsV2Page';
import PaymentsBillsWorkspace from './pages/payments/PaymentsBillsWorkspace';
import ServiceFeeReport from './pages/payments/ServiceFeeReport';
import VarianceReport from './pages/payments/VarianceReport';
import EmergencyBillUpload from './pages/payments/EmergencyBillUpload';
import UploadCreditFilePage from './pages/payments/UploadCreditFilePage';
import CreditDebitReportPage from './pages/payments/CreditDebitReportPage';
import GlobalCreditDebitPage from './pages/payments/GlobalCreditDebitPage';
import Bill2RollinPage from './pages/ubm/Bill2RollinPage';
import PropertyDetailsPage from './pages/ubm/PropertyDetailsPage';
import PropertyEditPage from './pages/ubm/PropertyEditPage';
import Bill2RollinEditPage from './pages/ubm/Bill2RollinEditPage';
import PaymentsDashboard from './pages/payments/PaymentsDashboard';
import InvoicesPage from './pages/invoices/InvoicesPage';
import SyncJobDetail from './pages/ubm/SyncJobDetail';
import JobRunDetail from './pages/ubm/JobRunDetail';
import BillsPage from './pages/bills/BillsPage';
import BillViewPage from './pages/bills/BillViewPage';
import BillsDashboardPage from './pages/bills/BillsDashboardPage';
import { isAuthenticated } from './lib/auth';
// --- Marketing website (public) ---
import MarketingLayout from './marketing/MarketingLayout';
import Home from './marketing/pages/Home';
import Platform from './marketing/pages/Platform';
import Astrid from './marketing/pages/Astrid';
import OnboardingPlanner from './marketing/pages/OnboardingPlanner';
import Signup from './marketing/pages/Signup';
import SignupSuccess from './marketing/pages/SignupSuccess';
import Pricing from './marketing/pages/Pricing';
import Solutions from './marketing/pages/Solutions';
import SolutionDetail from './marketing/pages/SolutionDetail';
import CaseStudies from './marketing/pages/CaseStudies';
import CaseStudyDetail from './marketing/pages/CaseStudyDetail';
import Blog from './marketing/pages/Blog';
import BlogPost from './marketing/pages/BlogPost';
import HowItWorks from './marketing/pages/HowItWorks';
import AboutPage from './marketing/pages/About';
import Demo from './marketing/pages/Demo';
import BookACall from './marketing/pages/BookACall';
import ContactPage from './marketing/pages/Contact';
import Support from './marketing/pages/Support';
import Faq from './marketing/pages/Faq';
import Partners from './marketing/pages/Partners';
import LandingPage from './marketing/pages/LandingPage';
import NotFound from './marketing/pages/NotFound';
import Legal from './marketing/pages/Legal';
import AdminApp from './marketing/admin/AdminApp';

function App() {
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={isAuthenticated() ? <Navigate to="/dashboard" /> : <Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <StatsBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bill-jobs"
            element={
              <ProtectedRoute>
                <BillJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-downloads"
            element={
              <ProtectedRoute>
                <BulkDownloads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/viewer/:documentId"
            element={
              <ProtectedRoute>
                <Viewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consolidated-viewer/:documentId"
            element={
              <ProtectedRoute>
                <ConsolidatedViewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute>
                <SuperAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cockpit"
            element={
              <ProtectedRoute>
                <Cockpit />
              </ProtectedRoute>
            }
          />
          {/* UBM Routes */}
          <Route
            path="/ubm/properties"
            element={
              <ProtectedRoute>
                <UbmLocations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/units"
            element={
              <ProtectedRoute>
                <UbmSpaces />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/meters"
            element={
              <ProtectedRoute>
                <UbmMeters />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/vendors"
            element={
              <ProtectedRoute>
                <UbmVendorAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/entities"
            element={
              <ProtectedRoute>
                <EntitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin"
            element={
              <ProtectedRoute>
                <Bill2RollinPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/property/:propertyId"
            element={
              <ProtectedRoute>
                <PropertyDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/edit/:accountIdRef"
            element={
              <ProtectedRoute>
                <Bill2RollinEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/edit-property/:propertyId"
            element={
              <ProtectedRoute>
                <PropertyEditPage />
              </ProtectedRoute>
            }
          />
          {/* Payments V2 Routes */}
          <Route
            path="/payments/dashboard"
            element={
              <ProtectedRoute>
                <PaymentsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/bills"
            element={
              <ProtectedRoute>
                <PaymentsBillsWorkspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/report"
            element={
              <ProtectedRoute>
                <PaymentsDashboard isGlobal={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/workspace"
            element={
              <ProtectedRoute>
                <PaymentsBillsWorkspace isGlobal={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <PaymentsV2Page />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/service-fees"
            element={
              <ProtectedRoute>
                <ServiceFeeReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/variance"
            element={
              <ProtectedRoute>
                <VarianceReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/emergency-bill-upload"
            element={
              <ProtectedRoute>
                <EmergencyBillUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/credit-debit"
            element={
              <ProtectedRoute>
                <CreditDebitReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/upload-credit-file"
            element={
              <ProtectedRoute>
                <UploadCreditFilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/global-credit-debit"
            element={
              <ProtectedRoute>
                <GlobalCreditDebitPage />
              </ProtectedRoute>
            }
          />
          {/* Invoices Route */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoicesPage />
              </ProtectedRoute>
            }
          />
          {/* Bills Module Routes */}
          <Route
            path="/bills"
            element={
              <ProtectedRoute>
                <BillsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/dashboard"
            element={
              <ProtectedRoute>
                <BillsDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/:billId"
            element={
              <ProtectedRoute>
                <BillViewPage />
              </ProtectedRoute>
            }
          />
          {/* Sync Job Detail Route (opens in new tab) */}
          <Route
            path="/sync-job/:jobId"
            element={
              <ProtectedRoute>
                <SyncJobDetail />
              </ProtectedRoute>
            }
          />
          {/* Job Run Detail Route (opens in new tab) */}
          <Route
            path="/job-run/:runId"
            element={
              <ProtectedRoute>
                <JobRunDetail />
              </ProtectedRoute>
            }
          />
          {/* Payment Verification Routes (V1 - Legacy) */}
          <Route
            path="/payment-verification"
            element={
              <ProtectedRoute>
                <PaymentVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-verification/config"
            element={
              <ProtectedRoute>
                <PaymentConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-verification/:fileId"
            element={
              <ProtectedRoute>
                <PaymentFileView />
              </ProtectedRoute>
            }
          />
          {/* Marketing website admin (independent auth) */}
          <Route path="/admin/*" element={<AdminApp />} />

          {/* Public marketing website */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/platform/astrid" element={<Astrid />} />
            <Route path="/platform/onboarding-planner" element={<OnboardingPlanner />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/success" element={<SignupSuccess />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/solutions/:slug" element={<SolutionDetail />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/book-a-call" element={<BookACall />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/support" element={<Support />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/lp/:slug" element={<LandingPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;
